import React, { useState, useEffect, useRef } from 'react';
import { AnimatedSection } from './AnimatedSection';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface Review {
    id: string;
    service: string;
    original_filename: string;
    processed_filename: string;
    processed_path: string;
    thumbnail_path: string;
    watermark_applied: boolean;
    date_processed: string;
    review_date?: string;
}

interface InfiniteScrollReviewsProps {
    service?: 'Algo Engine' | 'Blue Strike' | 'Copy Service';
    title?: string;
    autoScroll?: boolean; // Kept for API compatibility but unused in this version
    scrollSpeed?: number;
}

export const InfiniteScrollReviews: React.FC<InfiniteScrollReviewsProps> = ({
    service,
    title = 'Risultati e Feedback',
    scrollSpeed = 30
}) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollDirection = useRef<number>(0);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const { language } = useLanguage();
    const localeMap: Record<string, string> = { it: 'it-IT', en: 'en-GB', et: 'et-EE' };
    const dateLocale = localeMap[language] || 'it-IT';

    useEffect(() => {
        fetch('/assets/reviews/reviews-data.json')
            .then(res => res.json())
            .then((data: Review[]) => {
                let filtered = service ? data.filter(r => r.service === service) : data;
                // Sort chronologically by review_date ascending
                filtered = filtered.sort((a, b) => {
                    const da = a.review_date || a.date_processed || '';
                    const db = b.review_date || b.date_processed || '';
                    return da.localeCompare(db);
                });
                // Duplicate for infinite scroll effect (2 sets like HomePage)
                setReviews([...filtered, ...filtered]);
            })
            .catch(err => console.error('Error loading reviews:', err));
    }, [service]);

    useEffect(() => {
        let animationFrameId: number;

        const scrollLoop = () => {
            if (scrollContainerRef.current && scrollDirection.current !== 0) {
                const container = scrollContainerRef.current;
                const scrollAmount = 5 * scrollDirection.current;
                container.scrollLeft += scrollAmount;

                // Infinite loop logic
                if (scrollDirection.current === 1) { // Moving right
                    if (container.scrollLeft >= container.scrollWidth / 2) {
                        container.scrollLeft = 0;
                    }
                } else if (scrollDirection.current === -1) { // Moving left
                    if (container.scrollLeft <= 0) {
                        container.scrollLeft = container.scrollWidth / 2;
                    }
                }
            }
            animationFrameId = requestAnimationFrame(scrollLoop);
        };

        animationFrameId = requestAnimationFrame(scrollLoop);
        return () => cancelAnimationFrame(animationFrameId);
    }, [reviews]); // Re-run when reviews (content width) changes

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;

        const { left, width } = scrollContainerRef.current.getBoundingClientRect();
        const x = e.clientX - left;
        const threshold = width * 0.15; // 15% active area on sides

        if (x < threshold) {
            scrollDirection.current = -1;
            setShowLeftArrow(true);
            setShowRightArrow(false);
        } else if (x > width - threshold) {
            scrollDirection.current = 1;
            setShowLeftArrow(false);
            setShowRightArrow(true);
        } else {
            scrollDirection.current = 0;
            setShowLeftArrow(false);
            setShowRightArrow(false);
        }
    };

    const handleMouseLeave = () => {
        scrollDirection.current = 0;
        setShowLeftArrow(false);
        setShowRightArrow(false);
    };

    const handleNext = () => {
        if (selectedIndex === null) return;
        setSelectedIndex((prev) => (prev !== null && prev < reviews.length - 1 ? prev + 1 : 0));
    };

    const handlePrev = () => {
        if (selectedIndex === null) return;
        setSelectedIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : reviews.length - 1));
    };

    if (reviews.length === 0) {
        return null;
    }

    return (
        <section className="mb-24 overflow-hidden">
            <AnimatedSection>
                <h2 className="text-3xl font-bold mb-10 text-center uppercase tracking-wide">
                    <span className="text-green-500">✓</span> {title}
                </h2>

                <div className="relative group">
                    {/* Arrow Indicators */}
                    <div className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 transition-opacity duration-300 pointer-events-none ${showLeftArrow ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="bg-black/50 backdrop-blur-sm p-3 rounded-r-xl border-y border-r border-white/10 text-white/50">
                            <ChevronLeft size={48} className="animate-pulse" />
                        </div>
                    </div>
                    <div className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 transition-opacity duration-300 pointer-events-none ${showRightArrow ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="bg-black/50 backdrop-blur-sm p-3 rounded-l-xl border-y border-l border-white/10 text-white/50">
                            <ChevronRight size={48} className="animate-pulse" />
                        </div>
                    </div>

                    <div
                        ref={scrollContainerRef}
                        className="flex gap-6 overflow-x-auto scrollbar-hide py-4"
                        style={{ scrollBehavior: 'auto' }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                    >
                        {reviews.map((review, index) => (
                            <div
                                key={`${review.id}-${index}`}
                                className="flex-shrink-0 w-72 bg-[#151E32] border border-gray-800 rounded-2xl overflow-hidden hover:border-green-500/30 transition-all hover:-translate-y-2 hover:shadow-xl shadow-lg"
                            >
                                {/* Vertical Aspect Ratio (Phone format) - Full Screenshot Visible */}
                                <div className="relative aspect-[9/16] overflow-hidden bg-[#0B1120] flex items-center justify-center">
                                    {/* Full Screenshot (readable content, no zoom) */}
                                    <img
                                        src={review.processed_path}
                                        alt={`Screenshot ${review.service}`}
                                        className="w-full h-full object-contain transition-transform duration-500 hover:scale-105"
                                        loading="lazy"
                                    />

                                    <div className="absolute top-3 right-3 bg-green-500/90 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md backdrop-blur-sm z-20">
                                        <CheckCircle2 size={12} />
                                        VERIFICATO
                                    </div>
                                </div>

                                <div className="p-4 bg-[#151E32] relative z-20 border-t border-gray-800">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-300">{review.service}</span>
                                        <span className="text-xs text-gray-500 bg-gray-800/50 px-2 py-1 rounded-md">
                                            {new Date(review.review_date || review.date_processed).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>


                </div>
            </AnimatedSection>

            <style>{`
            .scrollbar-hide::-webkit-scrollbar {
                display: none;
            }
            .scrollbar-hide {
                -ms-overflow-style: none;
                scrollbar-width: none;
            }
            `}</style>
        </section>
    );
};

// Sub-component for clean state management of the lightbox
const ReviewLightbox: React.FC<{ image: string; onClose: () => void; onNext: () => void; onPrev: () => void }> = ({ image, onClose, onNext, onPrev }) => {

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') onNext();
            if (e.key === 'ArrowLeft') onPrev();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onNext, onPrev, onClose]);

    return (
        <div
            className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center backdrop-blur-md"
            onClick={onClose}
        >
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
                className="absolute top-6 right-6 text-white/70 hover:text-white hover:bg-white/10 transition-all z-[120] w-12 h-12 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-sm border border-white/10"
            >
                <span className="text-4xl leading-none pt-1">&times;</span>
            </button>

            {/* Prev Button */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onPrev();
                }}
                className="absolute left-2 md:left-8 text-white/50 hover:text-white transition-all z-[120] p-4 hover:bg-white/5 rounded-full"
            >
                <span className="text-5xl md:text-6xl font-thin">‹</span>
            </button>

            <img
                src={image}
                alt="Screenshot ingrandito"
                className="max-h-[98vh] w-auto max-w-[98vw] object-contain shadow-2xl animate-in fade-in zoom-in duration-300 select-none"
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
            />

            {/* Next Button */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                }}
                className="absolute right-2 md:right-8 text-white/50 hover:text-white transition-all z-[120] p-4 hover:bg-white/5 rounded-full"
            >
                <span className="text-5xl md:text-6xl font-thin">›</span>
            </button>
        </div>
    );
};
